import Breadcrumb from "components/common/Breadcrumb";
import CustomButton from "components/common/Button/CustomButton";
import Header from "components/common/Header/Header";
import {
  ROSTERING_BREADCRUMB,
  ROSTERING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import { LoginContext } from "helpers/LoginContext";
import SideBar from "components/common/SideBar/SideBarNew";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  emptyPaginationObj,
  LOADER_STATUS,
  POLICIES_LIST_LIMIT,
  SORT_TYPE,
} from "Constants/constant";
import TableHeader from "components/common/Table/TableHeader";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import {
  addMonths,
  baseURLImage,
  convertTo12HourFormat,
  getNameProfile,
  getPaginationObj,
} from "Utils/utils";
import { addFiltersAndPagination, apiPostRequest } from "helpers/Requests";
import { notifyPendingLeaveChanged } from "helpers/pendingLeaveBadge";
import { toast } from "react-toastify";
import LeaveActionModal from "./LeaveActionModal";
import { HiOutlineCheck, HiOutlineX } from "react-icons/hi";
import PaginationComponent from "../../../components/common/Pagination/index";
import { parseISO, format } from "date-fns";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import SelectOption from "components/common/Select/Select";
import SelectBox from "components/common/DropDownWithIocnsForPrint copy/DropDown";
import { BsFilterCircle } from "react-icons/bs";
import { FiDelete } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getHRMTeamManagmentRequest } from "store/actions/sp.actions";
import { getTeamManagmentList } from "store/selector/sp.selector";
import { downloadReportExportFile } from "pages/Reporting/reportExportUtils";

const LEAVE_TYPE_OPTIONS = [
  { label: "Partial Leave", value: "partial" },
  { label: "Full Day Leave", value: "full" },
];

const LEAVE_STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Expired", value: "expired" },
];

const SHIFT_RELEASE_SKIP_REASONS = {
  already_started: "worker already started",
  timesheet_locked: "timesheet submitted or approved",
  shift_invoiced: "shift already invoiced",
};

const LeaveRequest = (props) => {
  const { queryParams, loading } = props;
  const dispatch = useDispatch();
  const teamManagementList = useSelector(getTeamManagmentList);
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isLeaveActionSubmitting, setIsLeaveActionSubmitting] = useState(false);
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.INITIAL);
  const [leaves, setLeaves] = useState([]);

  const [appliedFilters, setAppliedFilters] = useState({
    leave_type: null,
    date_range: null,
    workers: [],
    status: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDescending, setSortDescending] = useState(true);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  useEffect(() => {
    dispatch(
      getHRMTeamManagmentRequest({
        filters: { page: 1, per_page: 500, search: null },
        bodyFilter: null,
      })
    );
  }, [dispatch]);

  const workerOptions = useMemo(() => {
    const workers =
      teamManagementList?.employees?.data?.map((item) => ({
        label: `${item?.first_name || ""} ${item?.last_name || ""}`.trim(),
        value: item?.id,
      })) || [];
    return workers.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    );
  }, [teamManagementList]);

  const getFilterValues = () => ({
    leave_type: selectedLeaveType?.value || null,
    date_range: fromDate && toDate ? [fromDate, toDate] : null,
    workers: selectedWorkers.map((w) => w.value),
    status: selectedStatuses.map((s) => s.value),
  });

  const buildFiltersPayload = (filters, overrides = {}) => {
    const payload = {
      page: overrides.page ?? paginationObj.page ?? 1,
      per_page: POLICIES_LIST_LIMIT,
      sort_field: sortBy || "created_at",
      sort_order: sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
      ...overrides,
    };

    if (filters.date_range) {
      payload.date_range = filters.date_range;
    }
    if (filters.workers?.length > 0) {
      payload.workers = filters.workers;
    }
    if (filters.leave_type) {
      payload.leave_type = filters.leave_type;
    }
    if (filters.status?.length > 0) {
      payload.status = filters.status;
    }

    return payload;
  };

  const openActionModal = (leave, action) => {
    setSelectedLeave(leave);
    setActionType(action);
    setIsModalOpen(true);
  };

  /**
   * Approving a leave opens the worker's overlapping shifts, but some cannot be
   * released (already started, locked timesheet, invoiced). The leave is still
   * approved, so the manager needs to know which shifts to reassign by hand.
   */
  const notifyUnreleasedShifts = (shiftRelease) => {
    const skipped = shiftRelease?.skipped || [];
    if (!skipped.length) return;

    const summary = skipped
      .map(
        (shift) =>
          `${shift.shift_date} (${
            SHIFT_RELEASE_SKIP_REASONS[shift.reason] || shift.reason
          })`
      )
      .join(", ");

    toast.warning(
      `${skipped.length} shift${
        skipped.length > 1 ? "s" : ""
      } could not be released and still need a worker assigned manually: ${summary}`,
      { autoClose: 10000 }
    );
  };

  const confirmAction = async (rejection_notes) => {
    if (!selectedLeave || !actionType) return;
    setIsLeaveActionSubmitting(true);
    try {
      const payload = {
        leave_id: selectedLeave.id,
        status: actionType,
        rejection_notes: rejection_notes ?? null,
      };
      if (actionType === "approved") {
        payload.acknowledge_scheduled_shifts = true;
      }

      const res = await apiPostRequest("/sp/worker-update-leave", payload);
      if (res.status === 200) {
        toast.success(
          `Leave ${
            actionType === "approved" ? "approved" : "rejected"
          } successfully`
        );
        notifyPendingLeaveChanged();
        notifyUnreleasedShifts(res?.data?.data?.shift_release);
        fetchLeaveRequestList(appliedFilters);
      } else {
        toast.error("Failed to update leave status");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Error updating leave status";
      toast.error(msg);
    } finally {
      setIsModalOpen(false);
      setSelectedLeave(null);
      setActionType(null);
      setIsLeaveActionSubmitting(false);
    }
  };

  const handleImageError = (e, fname, lname) => {
    return (e.target.src = getNameProfile(fname, lname));
  };

  useEffect(() => {
    if (!loading && renderLoading === LOADER_STATUS.PENDING) {
      setRenderLoading(LOADER_STATUS.COMPLATE);
    }
  }, [loading]);

  const fetchLeaveRequestList = async (
    filters = appliedFilters,
    overrides = {},
    reportType = null
  ) => {
    const payload = buildFiltersPayload(filters, overrides);
    if (reportType) {
      payload.report = reportType === "xlsx" ? "excel" : reportType;
    }

    try {
      setIsLoading(true);
      const res = await apiPostRequest(
        addFiltersAndPagination("sp/worker-leave-report", payload),
        payload
      );
      if (res.status === 200) {
        if (reportType) {
          const exportUrl = res?.data?.data;
          if (exportUrl) {
            await downloadReportExportFile(exportUrl, {
              format: reportType === "xlsx" ? "xlsx" : "pdf",
              report_label: "Leave_Request_Report",
            });
          } else {
            toast.error("Export failed. No download URL returned.");
          }
        } else {
          const fetchedLeaves = res?.data?.data?.leaves?.data || [];
          setLeaves(fetchedLeaves);
          setPaginationObj(getPaginationObj(res?.data?.data?.leaves));
        }
      }
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err?.downloadHandled) {
        return;
      }
      const msg =
        err?.response?.data?.message ||
        (reportType ? "Error exporting leave report" : "Error fetching leave requests");
      toast.error(msg);
    }
  };

  useEffect(() => {
    fetchLeaveRequestList(appliedFilters, { page: 1 });
  }, []);

  const handlePagination = (page) => {
    const nextPage = Number(page) || 1;
    setPaginationObj((prev) => ({ ...prev, page: nextPage }));
    fetchLeaveRequestList(appliedFilters, { page: nextPage });
  };

  const handleSearch = () => {
    const nextFilters = getFilterValues();
    setAppliedFilters(nextFilters);
    setPaginationObj((prev) => ({ ...prev, page: 1 }));
    fetchLeaveRequestList(nextFilters, { page: 1 });
  };

  const clearFilters = () => {
    const emptyFilters = {
      leave_type: null,
      date_range: null,
      workers: [],
      status: [],
    };
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setSelectedWorkers([]);
    setSelectedLeaveType(null);
    setSelectedStatuses([]);
    setAppliedFilters(emptyFilters);
    setPaginationObj((prev) => ({ ...prev, page: 1 }));
    fetchLeaveRequestList(emptyFilters, { page: 1 });
  };

  const handleFileDownLoad = (reportType) => {
    fetchLeaveRequestList(appliedFilters, { page: 1 }, reportType);
  };

  const handleDateCancel = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
  };

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return "-";
    try {
      return format(parseISO(dateValue), "dd-MMM-yyyy");
    } catch {
      return format(new Date(dateValue), "dd-MMM-yyyy");
    }
  };

  const renderStatusBadge = (status) => {
    const normalized = (status || "").toLowerCase();
    const baseClass =
      "inline-flex items-center px-2 py-1 rounded text-sm font-semibold capitalize";

    if (normalized === "pending") {
      return (
        <span className={`${baseClass} bg-yellow-100 text-yellow-700`}>
          Pending
        </span>
      );
    }
    if (normalized === "expired") {
      return (
        <span className={`${baseClass} bg-red-100 text-red-700`}>
          Expired
        </span>
      );
    }
    if (normalized === "approved") {
      return (
        <span className={`${baseClass} bg-green-100 text-green-700`}>
          Approved
        </span>
      );
    }
    if (normalized === "rejected") {
      return (
        <span className={`${baseClass} bg-red-100 text-red-700`}>
          Rejected
        </span>
      );
    }
    return status ? (
      <span className={`${baseClass} bg-gray-100 text-gray-700`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    ) : (
      "-"
    );
  };

  return (
    <div className="min-h-full">
      <SideBar />
      <div
        className={`duration-300 flex flex-col ${
          sideBarPadding ? "lg:pl-[4rem]" : "lg:pl-64"
        }`}
      >
        <Header
          cssClass="z-[10]"
          breadcrumb={
            <Breadcrumb
              pages={ROSTERING_BREADCRUMB(
                ROSTERING_BREADCRUMB_PAGES.LEAVEREQUESTS
              )}
            />
          }
        />
        <div className="w-full mx-auto">
          <div className="sm:px-6 lg:px-8 py-4 bg-white sticky top-[70px] z-[6] border-b border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="w-full space-y-1">
                <label
                  htmlFor="from-date-to-to-date"
                  className="block text-sm font-medium text-gray-700"
                >
                  Select Leave Date Range
                </label>
                <DateRangePickerNew
                  isClearable={false}
                  handleCancel={handleDateCancel}
                  handleChange={(obj) => {
                    if (obj && obj.fromDate && obj.toDate) {
                      setFromDate(obj.fromDate);
                      setToDate(obj.toDate);
                    }
                  }}
                  fromDate={fromDate}
                  toDate={toDate}
                  setSelectedFromDate={setSelectedFromDate}
                  maxDate={addMonths(selectedFromDate, 12)}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">
                  Worker
                </label>
                <SelectOption
                  isMulti
                  isClearable
                  newDesign={true}
                  isSearch={true}
                  data={workerOptions}
                  selectedOption={selectedWorkers}
                  handleChange={(e) => setSelectedWorkers(e || [])}
                  placeHolder="All"
                  cssClass="w-full"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">
                  Leave Type
                </label>
                <SelectOption
                  newDesign={true}
                  data={LEAVE_TYPE_OPTIONS}
                  selectedOption={selectedLeaveType}
                  handleChange={(e) => setSelectedLeaveType(e)}
                  placeHolder="All"
                  cssClass="w-full"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">
                  Status
                </label>
                <SelectOption
                  isMulti
                  isClearable
                  isSearch={false}
                  newDesign={true}
                  data={LEAVE_STATUS_OPTIONS}
                  selectedOption={selectedStatuses}
                  handleChange={(e) =>
                    setSelectedStatuses(Array.isArray(e) ? e : e ? [e] : [])
                  }
                  placeHolder="All"
                  cssClass="w-full"
                />
              </div>

              <div className="lg:col-span-4 flex flex-wrap justify-end items-center gap-2 pt-2">
                <CustomButton
                  Icon={<BsFilterCircle />}
                  clickHandler={handleSearch}
                  label="Search"
                  showLoading={isLoading}
                  isDisabled={isLoading}
                />
                <CustomButton
                  isDisabled={isLoading}
                  Icon={<FiDelete />}
                  clickHandler={clearFilters}
                  label="Clear Filter"
                  cssClass="whitespace-nowrap"
                />
                <SelectBox
                  isUserGracePeriodOver={isUserGracePeriodOver}
                  isLoading={isLoading}
                  setReportType={(e) => {
                    if (isUserGracePeriodOver) return;
                    handleFileDownLoad(e);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="sm:px-6 lg:px-8 w-full">
            <div className="flex flex-col">
              <div className="overflow-hidden">
                <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <div className="table-wrp block overflow-x-auto">
                    <table className="w-full divide-y divide-gray-300">
                      <thead className="bg-blue-50 sticky z-[5]">
                        <tr>
                          <TableHeader
                            sortName="first_name"
                            isSort={false}
                            label="Worker Name"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Leave Type"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Leave Start Date"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Leave End Date"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Requested Date"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Status"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Approved By"
                          />
                          <TableHeader
                            cssClass="sm:pl-3"
                            isSort={false}
                            label="Approval Date"
                          />
                          <th
                            scope="col"
                            className="text-gray-900 py-4 pl-3 pr-4 text-left text-sm font-semibold"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white overflow-y-auto">
                        {isLoading ? (
                          <tr>
                            <td colSpan={9} className="py-10">
                              <div className="flex justify-center items-center">
                                <CircularLoader
                                  classes="flex justify-center items-center"
                                  classloader="loader-l"
                                />
                              </div>
                            </td>
                          </tr>
                        ) : (
                          leaves?.length > 0 &&
                          leaves.map((leave, index) => (
                            <tr
                              key={leave?.id || index}
                              className="even:bg-white odd:bg-gray-50"
                            >
                              <td className="whitespace-nowrap py-4 px-3 text-sm">
                                <div className="flex items-center">
                                  <div className="h-8 w-10 flex-shrink-0">
                                    <img
                                      onError={(e) =>
                                        handleImageError(
                                          e,
                                          leave?.worker?.first_name,
                                          leave?.worker?.last_name
                                        )
                                      }
                                      className="capitalize object-cover h-10 w-10 rounded-full"
                                      src={
                                        leave?.worker?.photo
                                          ? baseURLImage(leave?.worker?.photo)
                                          : getNameProfile(
                                              leave?.worker?.first_name,
                                              leave?.worker?.last_name || " "
                                            )
                                      }
                                      alt=""
                                    />
                                  </div>
                                  <div className="ml-4 capitalize">
                                    <p className="w-40 truncate capitalize text-sm">
                                      {`${leave?.worker?.first_name || ""} ${
                                        leave?.worker?.last_name || ""
                                      }`}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {leave?.leave_type ||
                                  (leave?.is_partial_leave
                                    ? "Partial Leave"
                                    : "Full Day Leave")}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDisplayDate(leave.start_date)}{" "}
                                {leave.is_partial_leave
                                  ? convertTo12HourFormat(leave?.start_time)
                                  : ""}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDisplayDate(leave.end_date)}{" "}
                                {leave.is_partial_leave
                                  ? convertTo12HourFormat(leave?.end_time)
                                  : ""}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDisplayDate(leave.requested_date)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {renderStatusBadge(leave.status)}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">
                                {leave.approved_by || "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {leave.status === "approved" && leave.approval_date
                                  ? formatDisplayDate(leave.approval_date)
                                  : "N/A"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                {leave.status === "pending" ? (
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={() =>
                                        openActionModal(leave, "approved")
                                      }
                                      className="p-1 rounded border border-green-600 text-green-600 hover:bg-green-50"
                                      disabled={
                                        isLoading || isLeaveActionSubmitting
                                      }
                                    >
                                      <div
                                        className="icon-check-wrapper"
                                        title="Approve"
                                      >
                                        <HiOutlineCheck className="icon-size" />
                                      </div>
                                    </button>
                                    <button
                                      onClick={() =>
                                        openActionModal(leave, "rejected")
                                      }
                                      className="p-1 rounded border border-red-600 text-red-600 hover:bg-red-50"
                                      disabled={
                                        isLoading || isLeaveActionSubmitting
                                      }
                                    >
                                      <div
                                        className="icon-x-wrapper"
                                        title="Reject"
                                      >
                                        <HiOutlineX className="icon-size" />
                                      </div>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}

                        {!isLoading && leaves?.length === 0 && (
                          <tr>
                            <td
                              colSpan={9}
                              className="py-6 text-center text-red-600"
                            >
                              No data found !
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {leaves?.length > 0 && (
            <div className="bg-white sm:px-6 lg:px-8 w-full py-4">
              <PaginationComponent
                page={paginationObj.page}
                activeListLength={leaves}
                limit={POLICIES_LIST_LIMIT}
                totalList={paginationObj.total}
                handlePagination={handlePagination}
                totalPages={paginationObj.totalPages}
              />
            </div>
          )}

          <LeaveActionModal
            isShow={isModalOpen}
            handleClose={() => {
              if (!isLeaveActionSubmitting) setIsModalOpen(false);
            }}
            leave={selectedLeave}
            action={actionType}
            handleConfirm={confirmAction}
            isSubmitting={isLeaveActionSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default LeaveRequest;
